/** Helper methods + a computed for the EFT data type */
import { OapProvider } from '@/data/oapProviders'
import { PlanEverplansPaymentType } from '@/enums/PlanEverplansPaymentType'
import { PlanOapPaymentType } from '@/enums/PlanOapPaymentType'
import { PlanPaymentType } from '@/enums/PlanPaymentType'
import { usePlanStore } from '@/stores/plan'
import type { EFT } from '@/types'
import { computed } from 'vue'

export type LabeledEft = {
	label: string
	eft: EFT
	planIndex: number
}

export function getBlankEft(): EFT {
	return {
		accountHolder: '',
		bankName: '',
		routingNumber: '',
		routingNumber_confirm: '',
		accountNumber: '',
		accountNumber_confirm: '',
		accountType: 'checking',
	}
}

/**
 * Normalize an EFT-like value to a flat EFT.
 * Guards against accidentally copying wrappers such as `{ paymentDetails: EFT }`.
 */
export function toFlatEft(eft: EFT): EFT {
	const raw = eft as EFT & { paymentDetails?: EFT }

	const source =
		raw.paymentDetails && !raw.accountNumber ? raw.paymentDetails : raw

	return {
		accountHolder: source.accountHolder ?? '',
		bankName: source.bankName ?? '',
		routingNumber: source.routingNumber ?? '',
		routingNumber_confirm:
			source.routingNumber_confirm ?? source.routingNumber ?? '',
		accountNumber: source.accountNumber ?? '',
		accountNumber_confirm:
			source.accountNumber_confirm ?? source.accountNumber ?? '',
		accountType: source.accountType ?? 'checking',
	}
}

export function isBlankEft(eft: EFT | undefined): boolean {
	if (!eft) {
		return true
	}

	const blank = getBlankEft()

	return Object.keys(eft).every(
		(key) => eft[key as keyof EFT] === blank[key as keyof EFT]
	)
}

export function etfsAreEqual(eftA: EFT, eftB: EFT) {
	return (
		eftA.accountNumber === eftB.accountNumber &&
		eftA.routingNumber === eftB.routingNumber
	)
}

export function useEft() {
	const planStore = usePlanStore()

	/** Wrap an EFT with a label and save the index of the plan */
	function labelEft(eft: EFT, planIndex: number): LabeledEft {
		const label =
			eft.bankName && eft.bankName.length > 0 ? eft.bankName : eft.accountHolder

		return {
			label,
			eft,
			planIndex,
		}
	}

	/** Returns true if the EFT is a viable account */
	function eftIsComplete(eft: EFT): boolean {
		return eft && eft.accountNumber?.length > 0 && eft.routingNumber?.length > 0
	}

	/** List of all EFT accounts in the session */
	const allSessionEfts = computed(() => {
		const accounts = planStore.plans.flatMap((plan, index) => {
			const accounts: Array<EFT> = []

			if (plan.payment.paymentType === PlanPaymentType.EFT) {
				accounts.push(plan.payment.paymentDetails.initialAccount)

				if (plan.payment.paymentDetails.ongoingAccount) {
					accounts.push(plan.payment.paymentDetails.ongoingAccount)
				}
			}

			if (
				plan.oapPayment &&
				plan.oapPayment.provider === OapProvider.SEPIO_GUARD &&
				plan.oapPayment.paymentPlan?.paymentType === PlanOapPaymentType.EFT
			) {
				accounts.push(plan.oapPayment.paymentPlan.paymentDetails)
			}

			if (
				plan.everplansPayment &&
				plan.everplansPayment.paymentType === PlanEverplansPaymentType.EFT
			) {
				accounts.push(plan.everplansPayment.paymentDetails)
			}

			return accounts.filter(eftIsComplete).map((eft) => labelEft(eft, index))
		})

		// Filter out EFTs that appear earlier in the list
		return accounts.filter(
			(eft: LabeledEft, index: number) =>
				!accounts
					.slice(0, index)
					.some((existingEft) => etfsAreEqual(existingEft.eft, eft.eft))
		)
	})

	return {
		allSessionEfts: allSessionEfts,
	}
}
